import { useRef } from "react";
import styled from "styled-components";
import SwiperCore, { Autoplay, Pagination, Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import PhoneMockup from "./PhoneMockup";

import { cls } from "src/theme/colors";
import { Arrow } from "src/assets/svgs";
import { Capture } from "src/assets/images/captures";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

interface PhoneCarouselProps {
  captures: Capture[];
}

// 휴대폰 목업 안의 앱 화면을 한 장씩 넘겨 보여준다.
function PhoneCarousel({ captures }: PhoneCarouselProps) {
  const swiperRef = useRef<SwiperCore | null>(null);
  const loopFixStartXRef = useRef(0);

  return (
    <S.Carousel>
      {/* 직접 넘긴 뒤에도 자동 넘김(autoplay)은 계속된다. */}
      <S.NavButton type="button" aria-label="이전 화면" onClick={() => swiperRef.current?.slidePrev()}>
        <Arrow />
      </S.NavButton>
      <S.NavButton
        type="button"
        aria-label="다음 화면"
        data-direction="next"
        onClick={() => swiperRef.current?.slideNext()}
      >
        <Arrow />
      </S.NavButton>
      <Swiper
        centeredSlides
        loop
        autoplay={{
          delay: 2500,
          disableOnInteraction: false,
        }}
        modules={[Autoplay, Pagination, Navigation]}
        className="mySwiper"
        width={410}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        onTouchStart={(swiper) => {
          // 화면 맞춤 배율로 축소된 만큼 드래그 이동량이 줄어드는 것을 보정한다.
          const { width } = swiper.el.getBoundingClientRect();
          if (width) swiper.params.touchRatio = swiper.el.offsetWidth / width;
        }}
        onBeforeLoopFix={(swiper) => {
          loopFixStartXRef.current = swiper.touches.startX;
        }}
        onLoopFix={(swiper) => {
          // 드래그 도중 loop 보정이 일어나면 Swiper 가 드래그 시작점을 슬라이드 px 만큼 옮기는데,
          // 포인터 이동량에는 touchRatio 가 곱해지므로 그만큼 나눠 줘야 슬라이드가 튀지 않는다.
          const shift = swiper.touches.startX - loopFixStartXRef.current;
          if (shift) swiper.touches.startX = loopFixStartXRef.current + shift / (swiper.params.touchRatio || 1);
        }}
      >
        {captures.map(({ src, alt }) => (
          <SwiperSlide key={src}>
            <PhoneMockup src={src} alt={alt} />
          </SwiperSlide>
        ))}
      </Swiper>
    </S.Carousel>
  );
}

export default PhoneCarousel;

const S = {
  Carousel: styled.div`
    position: relative;
    width: 410px;
    .swiper-wrapper {
      width: 410px;
    }
    /* 화면 맞춤 배율로 축소되면 슬라이드 경계가 소수점 픽셀에 걸려, 바로 붙어 있는 다음 휴대폰의 옆 버튼이 가는 선으로 비친다.
       휴대폰(385px) 오른쪽의 빈 여백만 조금 잘라 그 선을 가린다. */
    .swiper {
      clip-path: inset(0 4px 0 0);
    }
  `,
  NavButton: styled.button`
    position: absolute;
    top: 400px;
    left: -92px;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: ${cls.white};
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    cursor: pointer;
    transform: translateY(-50%);
    transition: background-color 0.2s;
    svg {
      width: 12px;
      height: 22px;
      margin-right: 3px;
    }
    &:hover {
      background: ${cls.blue0};
      path {
        stroke: ${cls.mainBlue5};
      }
    }
    &[data-direction="next"] {
      left: 413px;
      svg {
        margin: 0 0 0 3px;
        transform: rotate(180deg);
      }
    }
  `,
};
